import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation, useHistory, useRouterHelper } from '@shared/core';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { Assistance, Bed, Camping, MapMarker, Search } from '@withjoy/joykit/icons';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { Box, ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { StyledInputContainer } from '@withjoy/joykit/components/InputV1/StyledInputContainer';

import type { AccommodationLayoutItem, AddAccommodationsProps } from './AddAccommodations.types';
import { LayoutList } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutList';
import type { LayoutListItemProps } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutList/LayoutList.types';
import { ACCOMMODATIONS_ACTIONS, CUSTOM_PROPERTY_ADD_KEY } from '../../Accommodations.constants';
import { usePlanningRoutes } from '@apps/admin/routes/Planning/hooks/usePlanningRoutes';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { ADD_STAY_SEARCH_PARAM_KEY } from '@shared/utils/accommodations';
import { CUSTOM_PAGE_ACTIONS } from '../../../CustomComponents.constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { LocationInput } from '@shared/components/LocationInput';
import type { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { useHandleOnSelectHotel } from '../../hooks/useHandleOnSelectHotel';
import type { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import type { CustomPropertyFormFields } from '../CustomProperty/CustomPropertyForm.types';

export const AddAccommodations: React.FC<AddAccommodationsProps> = props => {
  const { buildPath } = useRouterHelper();
  const { tq } = useTranslation('accommodationsAdmin');
  const { navigateToScreen, replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { hotelBlockPath } = usePlanningRoutes();
  const history = useHistory();
  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const isAddCustomPropertyOpen = queryParamHelper.getValueString(CUSTOM_PROPERTY_ADD_KEY);
  const telemetry = useAdminAccommodationsTelemetry();

  const { setCustomComponentBlockData, setMatchedHotel, setInitialRoomBlockEnabled } = useCustomComponentStateContext();

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);

  // Navigate directly to Add Hotel flow when a match is found
  // Uses navigateToScreen to maintain proper history: ADD → HOTEL_ADD
  // Back button will return to this screen (ACCOMMODATIONS_ADD)
  const navigateToAddHotelWithMatch = useEventCallback((hotelInventory: HotelInventory) => {
    // Reset loading state before navigation to avoid UI flash after navigation completes
    setIsSearching(false);
    setMatchedHotel(hotelInventory);
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_ADD);
  });

  // Navigate to CustomPropertyAdd with prefilled location data (no match found)
  const navigateToCustomPropertyWithPrefilledData = useEventCallback((formData: CustomPropertyFormFields) => {
    // Reset loading state before navigation to avoid UI flash after navigation completes
    setIsSearching(false);
    // Reset room block flag since this is not a room-block flow
    setInitialRoomBlockEnabled(false);
    setCustomComponentBlockData({
      __typename: 'AccommodationPlaceV2',
      name: formData.customName,
      customName: formData.customName,
      address: formData.address,
      customAddress: formData.customAddress,
      googlePlaceId: formData.googlePlaceId,
      lat: formData.latitude,
      long: formData.longitude,
      country: formData.country,
      postalCode: formData.postalCode,
      joyPlaceId: formData.joyPlaceId,
      zhId: formData.zhId,
      photo: formData.photoUrl ? { url: formData.photoUrl } : null,
    });
    handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsCustom, 'Search Location');
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
  });

  // Called when a hotel match is found - directly navigate to add hotel flow
  const onShowMatchedHotel = useEventCallback((show: boolean, _sourceId?: string, hotelInventory?: HotelInventory) => {
    if (show && hotelInventory) {
      navigateToAddHotelWithMatch(hotelInventory);
    }
  });

  // Called when hotel details are fetched - if no match, navigate to custom property
  // Also handles error cases where API fails - navigates to custom property with partial data
  const updateFormAfterFetchHotelDetails = useEventCallback((updatedValues: CustomPropertyFormFields) => {
    if (!updatedValues.joyPlaceId) {
      navigateToCustomPropertyWithPrefilledData(updatedValues);
    }
  });

  const { handleOnSelectHotel } = useHandleOnSelectHotel({
    isHotelForm: false,
    setShowMessage: onShowMatchedHotel,
    updateFormAfterFetchHotelDetails,
    isUpdateCustomUrlLocally: true,
    isUpdateCustomName: true,
  });

  const handleLocationChange = useEventCallback((location: Location | null) => {
    setPendingLocation(location);
  });

  // Creates a properly initialized form fields object for the search
  // The hook will mutate this object to populate it with location data
  const createInitialFormFields = useEventCallback(
    (): CustomPropertyFormFields => ({
      name: null,
      address: null,
      url: null,
      country: null,
      googlePlaceId: null,
      latitude: null,
      longitude: null,
      joyPlaceId: null,
      isCustomLinkEnabled: false,
      // HotelRoomBlock required fields
      isBookingLinkDisabled: true,
    }),
  );

  const handleSearchClick = useEventCallback(() => {
    if (!pendingLocation) return;
    setIsSearching(true);
    const formFields = createInitialFormFields();
    handleOnSelectHotel(formFields, pendingLocation);
  });

  // Handle auto-navigation to CustomPropertyAdd when coming from BrowseHotels
  useEffect(() => {
    if (isAddCustomPropertyOpen === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
      updateSearchParam('isCustomAddProperty', null);
    }
  }, [navigateToScreen, updateSearchParam, isAddCustomPropertyOpen]);

  const handleTelemetryOnItemClick = useCallback((itemType?: TravelMapBlockType, label?: string) => {
    telemetry.addAccommodationItemClicked(props.pageSlug, { itemType, label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemListClicked = useCallback(
    (item: LayoutListItemProps) => {
      const itemId = item.id;
      switch (itemId) {
        case 'hotel': {
          handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsHotel, 'Add Hotel');
          // Reset room block flag when entering non-room-block flow
          setInitialRoomBlockEnabled(false);
          const params = new URLSearchParams({ [ADD_STAY_SEARCH_PARAM_KEY]: 'true' });
          history.push(buildPath(`${PlanningRoutes.accommodations}/browse?${params.toString()}`));
          break;
        }
        case 'get-room-block':
          handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsRoomblock, 'Get Hotel Room Block');
          history.push(hotelBlockPath);
          break;
        case 'room-block':
          // Navigate to CustomPropertyAdd with room block toggle enabled
          handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsRoomblock, 'Add Room Block');
          setInitialRoomBlockEnabled(true);
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
          break;
        case 'custom':
          handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsCustom, 'Other');
          // Reset room block flag when entering non-room-block flow
          setInitialRoomBlockEnabled(false);
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
          break;
      }
    },
    [buildPath, handleTelemetryOnItemClick, history, hotelBlockPath, navigateToScreen, setInitialRoomBlockEnabled],
  );

  const handleClose = useEventCallback(() => {
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const accommodationItems: ReadonlyArray<AccommodationLayoutItem> = [
    { id: 'hotel', title: tq`Browse Hotels on Map`, subtitle: tq`Help me find hotels and deals for my guests near venue.`, icon: MapMarker },
    { id: 'room-block', title: tq`Add Room Block`, subtitle: tq`Add room block you already secured for your guests.`, icon: Bed },
    { id: 'get-room-block', title: tq`Get Room Block`, subtitle: tq`Search for available rooms blocks with exclusive upgrades near your event.`, icon: Assistance },
    { id: 'custom', title: tq`Add Rental Home, Campsite or More`, subtitle: tq`Add a link to a rental home, campsite or any other unique stays.`, icon: Camping },
  ];

  const layoutItems = [
    {
      id: uuidv4(),
      title: '',
      children: accommodationItems,
    },
  ];

  const isSearchButtonEnabled = pendingLocation && !isSearching;

  const headerContent = (
    <Box width="100%">
      <Box
        marginBottom={4}
        fontWeight={600}
        typographyVariant="label3"
      >
        {tq`Search for a hotel you have in mind`}
      </Box>
      <Flex
        gap={3}
        width="100%"
      >
        <Box
          flex={1}
          __css={{
            '& > div': { width: '100% !important' },
            [`${StyledInputContainer}`]: {
              borderRadius: 999,
              _after: {
                borderRadius: 999,
              },
              input: {
                borderRadius: 999,
                outline: 0,
                color: 'mono14',
                _focus: { outline: 0 },
              },
            },
          }}
        >
          <LocationInput
            name="hotelSearch"
            onLocationChange={handleLocationChange}
            placeholder={tq`Renaissance Chelsea, New York`}
            loading={isSearching}
            includeMapIcon={false}
            placePredictionTypes={[]}
            allowCustomPlace={false}
          />
        </Box>
        <ButtonV2
          onClick={handleSearchClick}
          disabled={!isSearchButtonEnabled}
          intent="primary"
          shape="rounded"
          width={pxToRem(48)}
          height={pxToRem(48)}
          minWidth={pxToRem(48)}
          padding={0}
          opacity={isSearchButtonEnabled ? 1 : 0.6}
          backgroundColor={'newUIBrandPrimary'}
        >
          <Search
            size={20}
            color="white"
          />
        </ButtonV2>
      </Flex>
    </Box>
  );

  return (
    <LayoutList
      title={tq`Add Place to Stay`}
      subTitle={tq`Add a hotel suggestion for your guests to make their trip planning easier.`}
      loading={false}
      items={layoutItems}
      handleClose={handleClose}
      onItemClicked={onItemListClicked}
      headerContent={headerContent}
      itemsListLabel={tq`Alternatively, select from the options below`}
    />
  );
};
